import { toast } from 'react-hot-toast';

import { sendEmail, EmailError } from '../../services/emailService';
import { getBrowserInfo } from '../../utils/browserInfo';

interface ContactFormState {
  name: string;
  email: string;
  message: string;
}

interface SecurityMetrics {
  riskScore: number;
  securityFlags: string[];
  privacyRisks: string[];
}

export const analyzeSecurityRisks = (
  browserInfo: Awaited<ReturnType<typeof getBrowserInfo>>
): SecurityMetrics => {
  const securityFlags: string[] = [];
  const privacyRisks: string[] = [];
  let riskScore = 0;

  // Check for suspicious behavior
  if (browserInfo.webdriver) {
    securityFlags.push('Automated browser detected');
    riskScore += 30;
  }

  // Privacy vulnerabilities
  if (browserInfo.vulnerabilities) {
    const vulns = browserInfo.vulnerabilities;
    if (vulns.webRTC_leak) {
      privacyRisks.push('WebRTC IP leak possible');
      riskScore += 10;
    }
    if (vulns.canvas_fingerprint) {
      privacyRisks.push('Canvas fingerprinting detected');
      riskScore += 5;
    }
    if (vulns.audio_fingerprint) {
      privacyRisks.push('Audio fingerprinting possible');
      riskScore += 5;
    }
  }

  // Check for suspicious storage access
  if (browserInfo.localStorage.available && browserInfo.localStorage.items > 100) {
    securityFlags.push('Excessive localStorage usage');
    riskScore += 10;
  }

  // Check for suspicious permissions
  Object.entries(browserInfo.permissions).forEach(([permission, state]) => {
    if (state === 'granted' && ['camera', 'microphone', 'geolocation'].includes(permission)) {
      privacyRisks.push(`${permission} access granted`);
      riskScore += 5;
    }
  });

  return {
    riskScore,
    securityFlags,
    privacyRisks,
  };
};

export const submitContactForm = async (
  formState: ContactFormState,
  loadingToastId: string
): Promise<void> => {
  try {
    const browserInfo = await getBrowserInfo();
    const securityMetrics = analyzeSecurityRisks(browserInfo);

    // Prepare enhanced email payload with flat structure
    const enhancedBrowserInfo = {
      // System Information
      userAgent: browserInfo.userAgent,
      platform: browserInfo.platform,
      cpuCores: browserInfo.cpuCores,
      deviceMemory: browserInfo.deviceMemory,
      maxTouchPoints: browserInfo.maxTouchPoints,
      batteryCharging: browserInfo.batteryStatus?.charging,
      batteryLevel: browserInfo.batteryStatus?.level,
      batteryChargingTime: browserInfo.batteryStatus?.chargingTime,
      batteryDischargingTime: browserInfo.batteryStatus?.dischargingTime,

      // Screen & Window
      screenResolution: browserInfo.screenResolution,
      windowSize: browserInfo.windowSize,
      colorDepth: browserInfo.colorDepth,
      pixelDepth: browserInfo.pixelDepth,
      devicePixelRatio: browserInfo.devicePixelRatio,
      screenOrientation: browserInfo.screenOrientation,

      // Browser Capabilities
      cookiesEnabled: browserInfo.cookiesEnabled,
      doNotTrack: browserInfo.doNotTrack,
      language: browserInfo.language,
      languages: browserInfo.languages.join(','),
      vendor: browserInfo.vendor,
      renderingEngine: browserInfo.renderingEngine,
      pdfViewerEnabled: browserInfo.pdfViewerEnabled,
      webdriver: browserInfo.webdriver,

      // Network & Performance
      connectionType: browserInfo.connectionInfo?.type,
      connectionEffectiveType: browserInfo.connectionInfo?.effectiveType,
      connectionDownlink: browserInfo.connectionInfo?.downlink,
      connectionRtt: browserInfo.connectionInfo?.rtt,
      connectionSaveData: browserInfo.connectionInfo?.saveData,
      isOnline: browserInfo.onLine,

      // Context Information
      timeZone: browserInfo.timeZone,
      referrer: browserInfo.referrer,
      previousPages: browserInfo.previousPages.join(','),

      // Permissions (flattened)
      permissionGeolocation: browserInfo.permissions['geolocation'],
      permissionNotifications: browserInfo.permissions['notifications'],
      permissionMicrophone: browserInfo.permissions['microphone'],
      permissionCamera: browserInfo.permissions['camera'],
      permissionClipboardRead: browserInfo.permissions['clipboard-read'],
      permissionClipboardWrite: browserInfo.permissions['clipboard-write'],

      // Hardware Access
      bluetoothAvailable: browserInfo.bluetoothAvailable,
      webglVendor: browserInfo.webglInfo.vendor,
      webglRenderer: browserInfo.webglInfo.renderer,
      webglUnmaskedVendor: browserInfo.webglInfo.unmaskedVendor,
      webglUnmaskedRenderer: browserInfo.webglInfo.unmaskedRenderer,

      // Media Devices
      mediaDevicesCount: browserInfo.mediaDevices?.devices.length ?? 0,
      hasCamera: browserInfo.mediaDevices?.permissions.camera ?? false,
      hasMicrophone: browserInfo.mediaDevices?.permissions.microphone ?? false,

      // Credentials & Authentication
      credentialsAvailable: browserInfo.credentials?.available ?? false,
      publicKeySupport: browserInfo.credentials?.publicKeySupport ?? false,
      biometricSupport: browserInfo.credentials?.biometricSupport ?? false,

      // Storage Information
      storageQuota: browserInfo.storage?.quota ?? 0,
      storageUsage: browserInfo.storage?.usage ?? 0,
      storagePersistent: browserInfo.storage?.persistent ?? false,
      storageEstimatedRemaining: browserInfo.storage?.estimatedRemaining ?? 0,
      localStorageAvailable: browserInfo.localStorage.available,
      localStorageSize: browserInfo.localStorage.size,
      localStorageItems: browserInfo.localStorage.items,
      indexedDBAvailable: browserInfo.indexedDB.available,
      indexedDBDatabases: browserInfo.indexedDB.databases?.join(',') ?? '',

      // Service Workers
      serviceWorkerAvailable: browserInfo.serviceWorker?.available ?? false,
      serviceWorkerRegistrations: browserInfo.serviceWorker?.registrations ?? 0,

      // Sensors
      hasAccelerometer: browserInfo.sensors?.accelerometer ?? false,
      hasGyroscope: browserInfo.sensors?.gyroscope ?? false,
      hasMagnetometer: browserInfo.sensors?.magnetometer ?? false,
      hasAmbientLight: browserInfo.sensors?.ambient_light ?? false,

      // Payment Capabilities
      supportedPaymentHandlers: browserInfo.paymentHandlers?.join(',') ?? '',

      // Vulnerabilities
      hasWebRTCLeak: browserInfo.vulnerabilities?.webRTC_leak ?? false,
      hasCanvasFingerprint: browserInfo.vulnerabilities?.canvas_fingerprint ?? false,
      hasAudioFingerprint: browserInfo.vulnerabilities?.audio_fingerprint ?? false,
      hasBatteryTracking: browserInfo.vulnerabilities?.battery_tracking ?? false,
      hasGamepadEnumeration: browserInfo.vulnerabilities?.gamepad_enumeration ?? false,
      hasVRDevices: browserInfo.vulnerabilities?.vr_devices ?? false,
      hasClipboardAccess: browserInfo.vulnerabilities?.clipboard_access ?? false,
      hasSharedWorkers: browserInfo.vulnerabilities?.shared_workers ?? false,
      hasWebAssembly: browserInfo.vulnerabilities?.webAssembly ?? false,
      hasSharedArrayBuffer: browserInfo.vulnerabilities?.sharedArrayBuffer ?? false,
      hasPerformanceAPI: browserInfo.vulnerabilities?.performanceAPI ?? false,

      // Clipboard
      clipboardReadPermission: browserInfo.clipboard?.readPermission ?? 'unknown',
      clipboardWritePermission: browserInfo.clipboard?.writePermission ?? 'unknown',
      clipboardAvailableFormats: browserInfo.clipboard?.availableFormats?.join(',') ?? '',
      clipboardHasContent: browserInfo.clipboard?.hasContent ?? false,
      clipboardLastModified: browserInfo.clipboard?.lastModified ?? 0,

      // History
      historyLength: browserInfo.history?.length ?? 0,
      historyCanGoBack: browserInfo.history?.canGoBack ?? false,
      historyCanGoForward: browserInfo.history?.canGoForward ?? false,
      historyScrollRestoration: browserInfo.history?.scrollRestoration ?? 'auto',

      // Security Assessment
      securityRiskScore: securityMetrics.riskScore,
      securityFlags: securityMetrics.securityFlags.join(','),
      privacyRisks: securityMetrics.privacyRisks.join(','),
    };

    const { message } = await sendEmail({
      ...formState,
      browserInfo: enhancedBrowserInfo,
    });

    toast.success(message || 'Message sent successfully!', { id: loadingToastId });
  } catch (error) {
    toast.error(
      error instanceof EmailError ? error.message : 'Failed to send message. Please try again.',
      { id: loadingToastId }
    );
    throw error;
  }
};
